import { FC, useEffect, useState } from 'react';
import { CustomButton, CustomCheckbox, CustomModal, ImagePreview } from 'components';
import { CheckboxTickIcon, CircleCloseIcon, ExpandImageIcon } from 'assets/icons';
import DateUtils from 'utils/dateUtils';
import { ButtonType } from 'components/custom-button/CustomButton';
import { AttachmentFile, VoidFn } from 'types/baseTypes';

export interface AttachmentResponse {
  id: string;
  title: string;
  urlLink: string;
  url: string;
}

export interface AttachmentGroup {
  createdAt: string;
  createdBy: string;
  comment?: string;
  attachmentResponse: AttachmentResponse[];
}

export interface AttachmentSection {
  title: string;
  showCheckboxForTitle: boolean;
  showCheckboxForNote: boolean;
  attachments: AttachmentGroup[];
}

interface AttachmentLibraryProps {
  isOpen: boolean;
  sections: AttachmentSection[];
  selectedAttachments: AttachmentFile[];
  onClose: VoidFn;
  onSave: (attachments: AttachmentFile[]) => void;
  isLoading?: boolean;
}

const AttachmentLibrary: FC<AttachmentLibraryProps> = ({
  isOpen,
  sections,
  selectedAttachments,
  onClose,
  onSave,
  isLoading = false
}) => {
  const [fullAttachmentsList, setFullAttachmentsList] = useState<AttachmentFile[]>([]);
  const [showImagePreview, setShowImagePreview] = useState({
    images: [] as AttachmentFile[],
    selectedIndex: 0,
    isOpen: false
  });

  useEffect(() => {
    setFullAttachmentsList(selectedAttachments);
  }, [selectedAttachments]);

  const handleSave = () => {
    onSave(fullAttachmentsList);
  };

  const isAttachmentSelected = (attachment: AttachmentFile) => {
    return fullAttachmentsList.some(fullAttachment => fullAttachment.id === attachment.id);
  };

  const getSelectedAttachmentsCount = () => {
    return fullAttachmentsList.length;
  };

  const toggleAttachmentSelection = (attachment: AttachmentFile) => {
    if (isAttachmentSelected(attachment)) {
      setFullAttachmentsList(prev => prev.filter(item => item.id !== attachment.id));
      return;
    }
    setFullAttachmentsList(prev => [...prev, attachment]);
  };

  const isListFullySelected = (attachments: AttachmentFile[]) => {
    return attachments.every(isAttachmentSelected);
  };

  const toggleFullListSelection = (attachments: AttachmentFile[]) => {
    if (isListFullySelected(attachments)) {
      setFullAttachmentsList(prev => prev.filter(item => !attachments.some(attachment => attachment.id === item.id)));
    } else {
      const newAttachments = attachments.filter(attachment => !isAttachmentSelected(attachment));
      setFullAttachmentsList(prev => [...prev, ...newAttachments]);
    }
  };

  const handleShowImagePreview = (attachmentResponse: AttachmentFile[], index: number) => {
    setShowImagePreview({ images: attachmentResponse, selectedIndex: index, isOpen: true });
  };

  const handleCloseImagePreview = () => {
    setShowImagePreview({ images: [], selectedIndex: 0, isOpen: false });
  };

  const renderAttachmentGroups = ({
    title,
    showCheckboxForTitle,
    attachments,
    showCheckboxForNote
  }: AttachmentSection) => (
    <div className="rounded border border-borderGray">
      <div className="flex items-center justify-between bg-secondaryBtn py-2 px-4">
        <div className="text-17px font-semibold text-heading">{title}</div>
        {showCheckboxForTitle && attachments[0] && (
          <CustomCheckbox
            className="bg-primary"
            checkedStyle="!bg-secondary"
            id={`${title}-checkbox`}
            isChecked={isListFullySelected(attachments[0].attachmentResponse)}
            onClick={() => toggleFullListSelection(attachments[0].attachmentResponse)}>
            {isListFullySelected(attachments[0].attachmentResponse) && <CheckboxTickIcon />}
          </CustomCheckbox>
        )}
      </div>

      <div>
        {attachments.map((group, index) => (
          <div key={`${group.createdAt}-${index}`} className="mx-4 border-b border-x-borderGray py-4 last:border-b-0">
            <div className="flex items-center justify-between">
              <div className="text-sbase text-heading">
                {DateUtils.getDateInFormat({
                  date: new Date(group.createdAt),
                  dateFormat: 'dd MMM yyyy, hh:mm aa'
                })}{' '}
                by {group.createdBy}
              </div>
              {showCheckboxForNote && (
                <CustomCheckbox
                  id={`${group.createdAt}-checkbox`}
                  isChecked={isListFullySelected(group.attachmentResponse)}
                  onClick={() => toggleFullListSelection(group.attachmentResponse)}>
                  {isListFullySelected(group.attachmentResponse) && <CheckboxTickIcon />}
                </CustomCheckbox>
              )}
            </div>

            {group.comment && <div className="text-sbase text-primaryText">{group.comment}</div>}

            <div className="mt-4 space-y-3">
              {group.attachmentResponse.map((file, ind) => (
                <div className="flex w-full items-center gap-3" key={`${file.title}-${ind}`}>
                  <div
                    className="relative h-[60px] max-h-[60px] w-[60px] cursor-pointer"
                    onClick={() => handleShowImagePreview(group.attachmentResponse, ind)}>
                    <img
                      src={file.urlLink}
                      height={60}
                      width={60}
                      className="h-[60px] max-h-[60px] min-w-[60px] cursor-pointer rounded object-cover"
                      alt={file.title}
                    />
                    <ExpandImageIcon className="absolute top-1 right-[3px] cursor-pointer" />
                  </div>
                  <div className="text-sbase text-heading">{file.title}</div>
                  <div className="ml-auto">
                    <CustomCheckbox
                      id={`${file.id}-checkbox`}
                      isChecked={isAttachmentSelected(file)}
                      onClick={() => toggleAttachmentSelection(file)}>
                      {isAttachmentSelected(file) && <CheckboxTickIcon />}
                    </CustomCheckbox>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex h-[calc(90vh-165px)] items-center justify-center">
          <div className="text-17px font-semibold text-heading">Loading...</div>
        </div>
      );
    }

    if (sections.length === 0) {
      return (
        <div className="flex h-[calc(90vh-165px)] items-center justify-center">
          <div className="text-17px font-semibold text-heading">No attachments found</div>
        </div>
      );
    }

    return (
      <>
        <div className="customNormalScroll space-y-6 overflow-auto px-10 py-6" style={{ height: 'calc(90vh - 165px)' }}>
          {sections.map((section, index) => (
            <div key={index}>{renderAttachmentGroups(section)}</div>
          ))}
        </div>
        <div className="mt-auto flex items-center justify-end gap-4 border-t border-borderGray px-10 py-4">
          <CustomButton
            type={ButtonType.SECONDARY}
            className="h-10 w-[100px]"
            id="attachment-modal-cancel"
            onClick={onClose}>
            Cancel
          </CustomButton>
          <CustomButton
            type={ButtonType.PRIMARY}
            className="h-10 w-[100px]"
            id="attachment-modal-save"
            onClick={handleSave}>
            Save
          </CustomButton>
        </div>
      </>
    );
  };

  return (
    <CustomModal open={isOpen} width="w-full md:w-[611px]" height="h-full md:max-h-[90vh]">
      <div className="relative">
        <div className="border-b border-borderGray px-10 pt-4 pb-3">
          <div className="pb-2 text-xl font-bold text-heading">Attachment library</div>
          <CircleCloseIcon className="absolute top-3 right-2 cursor-pointer" onClick={onClose} />
          <div className="text-sbase text-accentText">{getSelectedAttachmentsCount()} attachment selected</div>
        </div>

        {renderContent()}
      </div>

      {showImagePreview.isOpen && (
        <ImagePreview
          onClose={handleCloseImagePreview}
          images={showImagePreview.images}
          selectedIndex={showImagePreview.selectedIndex}
        />
      )}
    </CustomModal>
  );
};

export default AttachmentLibrary;
